import { apiGet, APIURL, cancelSelectEvent, getCSSStyles, getEChartsTooltipTheme, getHeaderPinButtonHTML, getHeaderPinStateClass, getNoDataHTML, getWidgetDataURI, normalizeWidgetTheme, setupHeaderPin, toggleWidgetTheme } from '../../helpers';
import { InsiderFilingSpeedTrackerConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from '../../types';

interface FilingSpeedRecord {
    reporting_name: string;
    symbol: string;
    filing_date: string;
    transaction_date: string;
    transaction_amount: number;
    days_to_file: number;
    speed_category: 'Fast' | 'Normal' | 'Slow';
}

interface FilingSpeedCategoryData {
    count: number;
    data: FilingSpeedRecord[];
}

interface InsiderFilingSpeedApiData {
    parameters: {
        days_back: number;
        min_transaction_value: number;
        top_n_per_category: number;
        cutoff_date: string;
        current_date: string;
    };
    summary: {
        total_insiders: number;
        fast_count: number;
        normal_count: number;
        slow_count: number;
        avg_days_to_file: number;
        min_days_to_file: number;
        max_days_to_file: number;
        median_days_to_file: number;
    };
    fast_filers: FilingSpeedCategoryData;
    normal_filers: FilingSpeedCategoryData;
    slow_filers: FilingSpeedCategoryData;
}

interface ChartRow {
    label: string;
    symbol: string;
    name: string;
    days: number;
    amount: number;
    category: 'Fast' | 'Normal' | 'Slow';
}

export class TraderverseMarketInsiderFilingSpeedTracker {
    private container: HTMLElement;
    private config: InsiderFilingSpeedTrackerConfig;
    private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
    private shadowRoot: ShadowRoot;
    private chartRows: ChartRow[] = [];
    private apiData: InsiderFilingSpeedApiData | null = null;
    private widgetResizeObserver: ResizeObserver | null = null;
    private filters = {
        daysBack: 90 as number | '',
        minTransactionValue: 500000 as number | '',
        topNPerCategory: 10 as number | ''
    };
    private currentPage: number = 1;
    private readonly pageSize: number = 10;

    private get totalPages(): number {
        return Math.max(1, Math.ceil(this.chartRows.length / this.pageSize));
    }

    constructor(config: InsiderFilingSpeedTrackerConfig) {
        this.config = { ...this.getDefaultConfig(), ...config };
        this.config.theme = normalizeWidgetTheme(this.config.theme);
        this.applyConfigFilters();
        this.container = this.resolveContainer(config.container);

        if ((this.container as any).shadowRoot) {
            this.shadowRoot = (this.container as any).shadowRoot;
        } else {
            this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
        }
        this.init();

        this.widgetResizeObserver = new ResizeObserver(() => {
            this.updateDisplay();
        });
        setTimeout(() => {
            const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
            if (widget) this.widgetResizeObserver?.observe(widget);
        }, 0);
    }

    private getDefaultConfig(): Partial<InsiderFilingSpeedTrackerConfig> {
        return {
            headerPinned: true,
            theme: 'default-dark',
            width: '100%',
            height: '400px',
            autosize: true,
            daysBack: 90,
            minTransactionValue: 500000,
            topNPerCategory: 10
        };
    }

    private applyConfigFilters(): void {
        if (typeof this.config.daysBack === 'number') this.filters.daysBack = this.config.daysBack;
        if (typeof this.config.minTransactionValue === 'number') this.filters.minTransactionValue = this.config.minTransactionValue;
        if (typeof this.config.topNPerCategory === 'number') this.filters.topNPerCategory = this.config.topNPerCategory;
    }

    private resolveContainer(container: string | HTMLElement): HTMLElement {
        if (typeof container === 'string') {
            const el = document.getElementById(container) || document.querySelector(container);
            if (!el) throw new Error(`Container not found: ${container}`);
            return el as HTMLElement;
        }
        return container;
    }

    private async init(): Promise<void> {
        await this.createWidget();
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');
        await this.injectChartLibrary();
        await this.setupChart();
        if (widget) widget.classList.remove('loading');
        this.emit('ready', {});
        cancelSelectEvent(this.shadowRoot);
        this.config.onReady?.();
    }

    private async createWidget(): Promise<void> {
        let themeClass = 'defaultDarkMode';
        let dataTheme = 'default-dark';

        if (this.config.theme === 'white') {
            themeClass = 'whiteMode';
            dataTheme = 'white';
        } else if (this.config.theme === 'black') {
            themeClass = 'blackMode';
            dataTheme = 'black';
        } else if (this.config.theme === 'default-lite') {
            themeClass = 'defaultLightMode';
            dataTheme = 'default-lite';
        }

        const widgetHtml = `
        <div style="width: ${typeof this.config.width == "number" ? this.config.width + 'px' : this.config.width};height: ${typeof this.config.height == "number" ? this.config.height + 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
          <div class="headTop">
            <h4 class="title market">${this.getTitleIconSvg()} Insider Filing Speed Tracker</h4>
            <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
              <div class="item">
                <button class="filters-toggle" type="button">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12.1794 0.950592H1.72077C1.57285 0.950469 1.42802 0.991908 1.30363 1.06995C1.17925 1.14799 1.08058 1.25932 1.01944 1.39061C0.958302 1.5219 0.937286 1.66759 0.958909 1.81023C0.980533 1.95286 1.04388 2.0864 1.14136 2.19484L5.22108 6.73834C5.34378 6.8752 5.41132 7.05084 5.41114 7.23259V10.8881C5.41114 10.9463 5.42504 11.0037 5.45175 11.0558C5.47846 11.1079 5.51725 11.1532 5.56503 11.1881L7.87343 12.8756C7.93059 12.9174 7.99856 12.9428 8.06972 12.9491C8.14088 12.9553 8.21242 12.9421 8.27633 12.911C8.34023 12.8799 8.39398 12.832 8.43154 12.7727C8.46911 12.7135 8.489 12.6452 8.489 12.5756V7.23259C8.48882 7.05084 8.55636 6.8752 8.67906 6.73834L12.7588 2.19409C13.1943 1.70959 12.8403 0.950592 12.1794 0.950592Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                  </svg>
                </button>
              </div>
              <i class="pi pi-ellipsis-v"></i>
            </div>
          </div>
          <div class="inBody">
            <div class="optionsRow">
              <span></span>
              <div class="filtersBox sideBar hidden">
                <div class="item">
                  <label>Days Back</label>
                  <input type="number" class="filter-days-back" min="1" step="1" placeholder="e.g. 90" value="${this.filters.daysBack}" />
                </div>
                <div class="item">
                  <label>Min Transaction Value</label>
                  <input type="number" class="filter-min-transaction-value" min="0" step="1" placeholder="e.g. 500000" value="${this.filters.minTransactionValue}" />
                </div>
                <div class="item">
                  <label>Top N Per Category</label>
                  <input type="number" class="filter-top-n-per-category" min="1" step="1" placeholder="e.g. 10" value="${this.filters.topNPerCategory}" />
                </div>
              </div>
            </div>
            <div class="chartDv" id="symbolByAverage">
                <div id="chartContainer" class="chart" data-testing-id="insider-filing-speed-chart"></div>
                <div class="pagination-controls">
                    <button class="prev-page" type="button" aria-label="Previous page">&#9664; Previous</button>
                    <span class="page-indicator">Page <span class="page-current">1</span> of <span class="page-total">1</span></span>
                    <button class="next-page" type="button" aria-label="Next page">Next &#9654;</button>
                </div>
            </div>
          </div>
        </div>
    `;
        this.shadowRoot.innerHTML = widgetHtml;
        await this.attachStyles();
        this.setupEventListeners();
    }

    private setupEventListeners(): void {
        setupHeaderPin(this.shadowRoot, this.config);

        const filtersToggle = this.shadowRoot.querySelector('.filters-toggle') as HTMLButtonElement;
        const filtersBox = this.shadowRoot.querySelector('.filtersBox') as HTMLElement;
        if (filtersToggle && filtersBox) {
            filtersToggle.addEventListener('click', (e) => {
                e.stopPropagation();
                filtersBox.classList.toggle('hidden');
            });
        }

        const updateNumericFiltersAndFetch = () => {
            this.filters.daysBack = this.readNumberInput('.filter-days-back');
            this.filters.minTransactionValue = this.readNumberInput('.filter-min-transaction-value');
            this.filters.topNPerCategory = this.readNumberInput('.filter-top-n-per-category');
            this.setupChart();
        };

        [
            '.filter-days-back',
            '.filter-min-transaction-value',
            '.filter-top-n-per-category'
        ].forEach((selector) => {
            const input = this.shadowRoot.querySelector(selector) as HTMLInputElement | null;
            input?.addEventListener('change', updateNumericFiltersAndFetch);
        });

        const prevBtn = this.shadowRoot.querySelector('.prev-page') as HTMLButtonElement;
        const nextBtn = this.shadowRoot.querySelector('.next-page') as HTMLButtonElement;

        prevBtn?.addEventListener('click', () => {
            if (this.currentPage > 1) {
                this.currentPage--;
                this.updatePaginationUI();
                this.updateDisplay();
            }
        });

        nextBtn?.addEventListener('click', () => {
            if (this.currentPage < this.totalPages) {
                this.currentPage++;
                this.updatePaginationUI();
                this.updateDisplay();
            }
        });
    }

    private updatePaginationUI(): void {
        const currentEl = this.shadowRoot.querySelector('.page-current');
        const totalEl = this.shadowRoot.querySelector('.page-total');
        const prevBtn = this.shadowRoot.querySelector('.prev-page') as HTMLButtonElement;
        const nextBtn = this.shadowRoot.querySelector('.next-page') as HTMLButtonElement;

        if (currentEl) currentEl.textContent = this.currentPage.toString();
        if (totalEl) totalEl.textContent = this.totalPages.toString();
        if (prevBtn) prevBtn.disabled = this.currentPage <= 1;
        if (nextBtn) nextBtn.disabled = this.currentPage >= this.totalPages;
    }

    private readNumberInput(selector: string): number | '' {
        const input = this.shadowRoot.querySelector(selector) as HTMLInputElement | null;
        if (!input || input.value.trim() === '') return '';
        const value = parseFloat(input.value);
        return Number.isNaN(value) ? '' : value;
    }

    private buildQueryParams(): Record<string, string | number> {
        const query: Record<string, string | number> = {};
        if (this.filters.daysBack !== '') query.daysBack = this.filters.daysBack;
        if (this.filters.minTransactionValue !== '') query.minTransactionValue = this.filters.minTransactionValue;
        if (this.filters.topNPerCategory !== '') query.topNPerCategory = this.filters.topNPerCategory;
        return query;
    }

    private mapChartRows(data: InsiderFilingSpeedApiData | null | undefined): ChartRow[] {
        if (!data) return [];

        const categories: Array<{ key: 'Fast' | 'Normal' | 'Slow'; records: FilingSpeedRecord[] }> = [
            { key: 'Fast', records: data.fast_filers?.data ?? [] },
            { key: 'Normal', records: data.normal_filers?.data ?? [] },
            { key: 'Slow', records: data.slow_filers?.data ?? [] }
        ];

        return categories.flatMap(({ key, records }) =>
            records.map((record) => ({
                label: record.symbol,
                symbol: record.symbol,
                name: record.reporting_name,
                days: record.days_to_file,
                amount: record.transaction_amount,
                category: key
            }))
        );
    }

    private async attachStyles(): Promise<void> {
        try {
            const styleEl = await getCSSStyles();
            this.shadowRoot.appendChild(styleEl);
        } catch (err) {
            console.error('Failed to load CSS file:', err);
        }
    }

    private async injectChartLibrary(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if ((window as any).echarts) return resolve(true);
            if (this.shadowRoot.querySelector('script[data-chart-lib]')) {
                const script = this.shadowRoot.querySelector('script[data-chart-lib]') as HTMLScriptElement;
                script.onload = () => resolve(true);
            } else {
                const script = document.createElement('script');
                script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
                script.async = true;
                script.setAttribute('data-chart-lib', 'true');
                this.shadowRoot.appendChild(script);
                script.onload = () => resolve(true);
            }
        });
    }

    private async setupChart(): Promise<void> {
        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        const containerWrapper = this.shadowRoot.querySelector('#symbolByAverage') as HTMLElement;
        if (widgetBox) widgetBox.classList.add('loading');

        try {
            const res = await apiGet({
                method: 'GET',
                url: `${APIURL}/v1/equity-market/insider-filing-speed-tracker`,
                query: this.buildQueryParams(),
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (!res.ok) throw new Error(`API error: ${res.status}`);
            const json = await res.json();
            this.apiData = json?.data as InsiderFilingSpeedApiData | null;
            this.chartRows = this.mapChartRows(this.apiData);

            if (this.chartRows.length) {
                containerWrapper?.setAttribute('data-testing-id', 'insider-filing-speed-tracker');
            } else {
                containerWrapper?.removeAttribute('data-testing-id');
            }

            this.currentPage = 1;
            this.updatePaginationUI();
            this.updateDisplay();
            this.emit('dataUpdate', this.apiData);
        } catch (err) {
            console.error('Error fetching chart data:', err);
            this.chartRows = [];
            this.apiData = null;
            containerWrapper?.removeAttribute('data-testing-id');
            this.updateDisplay();
            this.emit('error', err);
        } finally {
            if (widgetBox) widgetBox.classList.remove('loading');
        }
    }

    private updateDisplay(): void {
        if (!(window as any).echarts) {
            setTimeout(() => this.updateDisplay(), 100);
            return;
        }
        this.drawChartByType();
    }

    private formatCurrency(value: number): string {
        const abs = Math.abs(value);
        if (abs >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
        if (abs >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
        if (abs >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
        if (abs >= 1e3) return `$${(value / 1e3).toFixed(2)}K`;
        return `$${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
    }

    private drawChartByType(): void {
        const echarts = (window as any).echarts;
        const container = this.shadowRoot.querySelector('#chartContainer') as HTMLDivElement;
        const containerWrapper = this.shadowRoot.querySelector('#symbolByAverage') as HTMLElement;

        if (!container) return;

        const instance = echarts.getInstanceByDom(container);
        if (instance) echarts.dispose(instance);

        const allData = this.chartRows;
        if (!allData.length) {
            containerWrapper?.removeAttribute('data-testing-id');
            container.innerHTML = getNoDataHTML();
            return;
        }

        const startIdx = (this.currentPage - 1) * this.pageSize;
        const data = allData.slice(startIdx, startIdx + this.pageSize);

        container.innerHTML = '';
        containerWrapper?.setAttribute('data-testing-id', 'insider-filing-speed-tracker');

        this.updatePaginationUI();

        const widgetBoxComputed = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        const textColor = getComputedStyle(widgetBoxComputed).getPropertyValue('--charts-text-color');
        const axisLineColor = getComputedStyle(widgetBoxComputed).getPropertyValue('--charts-axis-color');
        const splitLineColor = getComputedStyle(widgetBoxComputed).getPropertyValue('--charts-split-line-color');
        const fontSize = getComputedStyle(widgetBoxComputed).getPropertyValue('--font-size-sm');

        const xAxisData = data.map((item, index) => {
            const duplicateCount = data.slice(0, index).filter((row) => row.symbol === item.symbol).length;
            return duplicateCount > 0 ? `${item.symbol} (${item.category.charAt(0)})` : item.symbol;
        });
        const daysData = data.map((item) => item.days);
        const amountData = data.map((item) => item.amount);

        const maxAmount = Math.max(...amountData, 1);
        const maxDays = Math.max(...daysData, 0.01);

        const normalizedDays = daysData.map((days) => days / maxDays);
        const normalizedAmounts = amountData.map((amount) => amount / maxAmount);
        const combinedScores = normalizedDays.map((days, i) => (days + normalizedAmounts[i]) / 2);

        const bar1Data = normalizedDays;
        const bar2Data = normalizedAmounts;
        const bar3Data = combinedScores;

        const option = {
            backgroundColor: 'transparent',
            tooltip: {
                ...getEChartsTooltipTheme(widgetBoxComputed),
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                formatter: (params: any) => {
                    const index = params[0].dataIndex;
                    const row = data[index];
                    let html = `<b>${row.symbol}</b> — ${row.name}<br/>`;
                    html += `Category: <b>${row.category}</b><br/>`;
                    html += `Days to File: <b>${row.days}</b><br/>`;
                    html += `Transaction: <b>${this.formatCurrency(row.amount)}</b><br/>`;
                    return html;
                }
            },
            legend: {
                data: ['Days to File', 'Transaction Amount', 'Combined Score'],
                top: 0,
                textStyle: { color: textColor, fontSize: 11 }
            },
            grid: {
                left: 5,
                right: 5,
                top: 28,
                bottom: 5,
                containLabel: true
            },
            xAxis: {
                type: 'category',
                data: xAxisData,
                boundaryGap: true,
                axisLabel: {
                    color: textColor,
                    fontSize: 10,
                    interval: 0,
                    rotate: 0,
                    verticalAlign: 'top',
                    align: 'right',
                    margin: 4
                },
                axisTick: {
                    show: true
                },
                axisLine: {
                    show: true,
                    lineStyle: {
                        color: axisLineColor
                    }
                },
                splitLine: { show: false },
                z: 10
            },
            yAxis: {
                type: 'value',
                max: 1,
                axisLabel: {
                    color: textColor,
                    fontSize: fontSize,
                    formatter: (value: number) => value.toFixed(1)
                },
                axisTick: { show: true },
                axisLine: {
                    show: true,
                    lineStyle: {
                        color: axisLineColor
                    }
                },
                splitLine: {
                    show: true,
                    lineStyle: {
                        color: splitLineColor,
                        type: 'dashed'
                    }
                }
            },
            series: [
                {
                    name: 'Days to File',
                    type: 'bar',
                    data: bar1Data,
                    barWidth: '26%',
                    barGap: '20%',
                    label: {
                        show: false
                    },
                    itemStyle: {
                        color: '#00CAA9',
                        borderRadius: [4, 4, 0, 0]
                    }
                },
                {
                    name: 'Transaction Amount',
                    type: 'bar',
                    data: bar2Data,
                    barWidth: '26%',
                    barGap: '20%',
                    label: {
                        show: false
                    },
                    itemStyle: {
                        color: '#F59E0C',
                        borderRadius: [4, 4, 0, 0]
                    }
                },
                {
                    name: 'Combined Score',
                    type: 'bar',
                    data: bar3Data,
                    barWidth: '26%',
                    barGap: '20%',
                    label: {
                        show: false
                    },
                    itemStyle: {
                        color: '#EF4444',
                        borderRadius: [4, 4, 0, 0]
                    }
                }
            ]
        };

        const chart = echarts.init(container);
        chart.setOption(option);
    }

    public on(type: WidgetEventType, handler: WidgetEventHandler): void {
        if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
        this.eventHandlers.get(type)!.push(handler);
    }
    public off(type: WidgetEventType, handler: WidgetEventHandler): void {
        const list = this.eventHandlers.get(type);
        if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
    }
    public async getWidgetImage(): Promise<string> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        return await getWidgetDataURI(widget);
    }

    public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
        if (!this.shadowRoot) return;

        this.config.theme = toggleWidgetTheme(
            this.shadowRoot,
            theme
        ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';

        this.updateDisplay();
    }
    public destroy(): void {
        if (this.widgetResizeObserver) { this.widgetResizeObserver.disconnect(); this.widgetResizeObserver = null; }
        this.eventHandlers.clear(); this.shadowRoot.innerHTML = '';
    }
    private emit(type: WidgetEventType, data: any): void {
        const handlers = this.eventHandlers.get(type);
        if (handlers) { const evt: WidgetEvent = { type, data }; handlers.forEach(h => h(evt)); }
    }
    private getTitleIconSvg(): string {
      return `<svg width="27" height="22" viewBox="0 0 27 22" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M22.6125 22H4.3875C3.26681 22.0001 2.18858 21.5634 1.37399 20.7794C0.559395 19.9955 0.0701982 18.9239 0.00675018 17.7843L2.29482e-08 17.5312V3.09375C-9.45295e-05 2.30983 0.291999 1.5551 0.817249 0.982089C1.3425 0.409082 2.06173 0.0605304 2.8296 0.00687518L3.0375 2.33732e-08H19.9125C20.6822 -9.62801e-05 21.4232 0.297406 21.9858 0.832383C22.5484 1.36736 22.8906 2.09991 22.9433 2.882L22.95 3.09375V4.125H23.9625C24.7322 4.1249 25.4732 4.42241 26.0358 4.95738C26.5984 5.49236 26.9406 6.22491 26.9933 7.007L27 7.21875V17.5312C27.0001 18.6727 26.5713 19.7709 25.8016 20.6006C25.032 21.4302 23.9798 21.9285 22.8609 21.9931L22.6125 22ZM4.3875 19.9375H22.6125C23.2056 19.9374 23.777 19.7102 24.2132 19.3008C24.6494 18.8914 24.9184 18.33 24.9669 17.7279L24.975 17.5312V7.21875C24.975 6.96955 24.8864 6.72878 24.7256 6.54097C24.5647 6.35316 24.3426 6.23102 24.1002 6.19712L23.9625 6.1875H22.95V16.8438C22.95 17.093 22.8614 17.3337 22.7006 17.5215C22.5397 17.7093 22.3176 17.8315 22.0752 17.8654L21.9375 17.875C21.6928 17.875 21.4564 17.7847 21.272 17.6209C21.0876 17.4571 20.9677 17.2309 20.9345 16.984L20.925 16.8438V3.09375C20.925 2.84455 20.8364 2.60378 20.6756 2.41597C20.5147 2.22816 20.2926 2.10602 20.0502 2.07212L19.9125 2.0625H3.0375C2.79283 2.06251 2.55644 2.15276 2.37204 2.31656C2.18765 2.48035 2.06773 2.70661 2.03445 2.9535L2.025 3.09375V17.5312C2.02502 18.1351 2.24798 18.7169 2.64962 19.1612C3.05125 19.6054 3.6022 19.8796 4.1931 19.9292L4.3875 19.9375ZM13.8321 14.4375H17.8902C18.1467 14.4376 18.3937 14.5368 18.5811 14.7152C18.7686 14.8936 18.8826 15.1378 18.9 15.3985C18.9175 15.6591 18.8372 15.9169 18.6753 16.1195C18.5134 16.3222 18.282 16.4548 18.0279 16.4904L17.8902 16.5H13.8321C13.5756 16.4999 13.3286 16.4007 13.1412 16.2223C12.9537 16.0439 12.8398 15.7997 12.8223 15.539C12.8048 15.2784 12.8851 15.0206 13.047 14.818C13.2089 14.6153 13.4403 14.4827 13.6944 14.4471L13.8321 14.4375ZM9.77805 9.63188C10.0466 9.63188 10.3041 9.74052 10.494 9.93392C10.6839 10.1273 10.7906 10.3896 10.7906 10.6631V15.4688C10.7906 15.7423 10.6839 16.0046 10.494 16.198C10.3041 16.3914 10.0466 16.5 9.77805 16.5H5.0598C4.79127 16.5 4.53373 16.3914 4.34385 16.198C4.15397 16.0046 4.0473 15.7423 4.0473 15.4688V10.6631C4.0473 10.3896 4.15397 10.1273 4.34385 9.93392C4.53373 9.74052 4.79127 9.63188 5.0598 9.63188H9.77805ZM8.76555 11.6944H6.0723V14.4375H8.76555V11.6944ZM13.8321 9.63188H17.8902C18.1467 9.63196 18.3937 9.73121 18.5811 9.90959C18.7686 10.088 18.8826 10.3322 18.9 10.5928C18.9175 10.8535 18.8372 11.1112 18.6753 11.3139C18.5134 11.5166 18.282 11.6491 18.0279 11.6848L17.8902 11.6944H13.8321C13.5737 11.6971 13.3242 11.599 13.1344 11.4204C12.9447 11.2417 12.8293 10.996 12.8117 10.7334C12.7941 10.4709 12.8757 10.2114 13.0398 10.0082C13.2039 9.8049 13.4381 9.67324 13.6944 9.64012L13.8321 9.63188ZM5.0598 4.81525H17.8902C18.1486 4.81257 18.3981 4.9106 18.5879 5.08925C18.7776 5.2679 18.893 5.51366 18.9106 5.7762C18.9282 6.03875 18.8466 6.29821 18.6825 6.50147C18.5184 6.70472 18.2842 6.83639 18.0279 6.8695L17.8902 6.87775H5.0598C4.80327 6.87767 4.55633 6.77841 4.36888 6.60004C4.18143 6.42166 4.06745 6.17746 4.04997 5.91679C4.03248 5.65612 4.1128 5.3984 4.27468 5.19571C4.43657 4.99303 4.66796 4.86049 4.9221 4.82487L5.0598 4.81525Z" fill="currentColor"/> </svg>`;
    }

}

declare global { interface Window { TraderverseMarketInsiderFilingSpeedTracker: typeof TraderverseMarketInsiderFilingSpeedTracker; } }
if (typeof window !== 'undefined') { window.TraderverseMarketInsiderFilingSpeedTracker = TraderverseMarketInsiderFilingSpeedTracker; }
